import axios from "axios";
import {
  DEFAULT_START_OF_DAY,
  MAXIMUM_SAFE_DURATION,
  PDA_PREFIX,
  SUMMARY_TYPE,
  PROJECT_START_MILESTONE,
  PROJECT_FINISH_MILESTONE,
  CONSTRAINT_TYPE_LATEST,
  CONSTRAINT_TYPE_FINISH_NO_LATER_THAN,
  CONSTRAINT_TYPE_EARLIEST,
  CONSTRAINT_TYPE_START_NO_EARLIER_THAN,
  CONSTRAINT_TYPE_AS_LATE_AS_POSSIBLE,
  CONSTRAINT_TYPE_AS_LATE_AS_POSSIBLE_FRONT,
  CONSTRAINT_TYPE_ASAP,
  CONSTRAINT_TYPE_ASAP_FRONT,
} from "../../global/constants";
import { removeTimezoneOffset } from "../DateUtils/DateUtils";
import { fromDisplayDuration, fromDisplayWork } from "../displayUnits";
import { extractFieldWithRecursion } from "../ObjectUtils/ObjectUtils";
import { reconcileTaskParents } from "../../components/Bryntum/Gantt/insertTasksHierarchy";
import { REALIGN_SCHEDULE_ACTIVITY_SEQUENCE } from "../../services/types";

export const computeAvailableWork = (workPlanned, workAssigned) => {
  const result = workPlanned - workAssigned;
  if (result < 0) return 0;
  return result;
};

export const convertSchedulerTasks = (tasks) =>
  !!tasks.length
    ? tasks.map(({ id, name, start, finish, resource, ...rest }) => ({
      id,
      Name: name,
      StartTime: start,
      EndTime: finish,
      CollabsID: resource.id,
      resource,
      ...rest,
    }))
    : [];

export const getInitialAllocation = (
  numberOfResources,
  resourceIndex,
  lastItemIndex
) => {
  const isLastItem = resourceIndex === lastItemIndex;
  const value = Math.floor(100 / numberOfResources);
  const rest = 100 % numberOfResources;
  return isLastItem ? value + rest : value;
};

const constraintTypeParser = {
  [CONSTRAINT_TYPE_EARLIEST]: CONSTRAINT_TYPE_START_NO_EARLIER_THAN,
  [CONSTRAINT_TYPE_LATEST]: CONSTRAINT_TYPE_FINISH_NO_LATER_THAN,
  [CONSTRAINT_TYPE_AS_LATE_AS_POSSIBLE]: CONSTRAINT_TYPE_AS_LATE_AS_POSSIBLE_FRONT,
  [CONSTRAINT_TYPE_ASAP]: CONSTRAINT_TYPE_ASAP_FRONT,
};

export const parseConstraintType = (constraint) =>
  constraint && constraint.type in constraintTypeParser
    ? constraintTypeParser[constraint.type]
    : "";

/**
 * Parses a date value as a LOCAL date, preserving the calendar day from the
 * database regardless of the browser's timezone.
 *
 * Problem: Database stores dates as UTC midnight ("2026-03-18T00:00:00Z").
 * When parsed with `new Date()` in non-UTC timezones, the local date can shift:
 * - In negative offsets (e.g., UTC-8): March 18 00:00 UTC → March 17 16:00 local
 * - In positive offsets (e.g., UTC+1): March 18 00:00 UTC → March 18 01:00 local
 * After applying setHours(), the displayed date may be wrong.
 *
 * Solution: Extract the YYYY-MM-DD portion and create a Date at local midnight,
 * similar to ensureDate() in Grid/utils.js.
 */
const parseAsLocalDate = (value) => {
  if (value instanceof Date) return new Date(value);
  if (typeof value === "string") {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      return new Date(+match[1], +match[2] - 1, +match[3]);
    }
  }
  return new Date(value);
};

export const normalizeConstraintDate = (constraintDate, constraintType) => {
  if (!constraintDate) return constraintDate;

  // Parse as local date to preserve the calendar day from the database,
  // avoiding timezone-induced day shifts.
  const date = parseAsLocalDate(constraintDate);

  // Normalize constraint dates to standard business hours in local time.
  // Using setHours ensures activities display at correct times (e.g., 9:00 AM)
  // regardless of timezone differences.
  if (
    constraintType === CONSTRAINT_TYPE_LATEST ||
    constraintType === CONSTRAINT_TYPE_FINISH_NO_LATER_THAN
  ) {
    date.setHours(23, 59, 0, 0);
  } else if (
    constraintType === CONSTRAINT_TYPE_EARLIEST ||
    constraintType === CONSTRAINT_TYPE_START_NO_EARLIER_THAN ||
    constraintType === CONSTRAINT_TYPE_ASAP ||
    constraintType === CONSTRAINT_TYPE_ASAP_FRONT
  ) {
    date.setHours(DEFAULT_START_OF_DAY, 0, 0, 0);
  }

  return date;
};

/**
 * When the user manually edits the start date of an ALAP ("as late as
 * possible") task — via the grid Start cell, the task-edit dialog, or
 * by dragging the bar — the BE scheduler will recompute the task back
 * to its old `constraintDate` on save unless we also bump that field.
 *
 * Mutates the DTO in place so the existing call sites can `dto = format(...)`
 * → `syncAlapConstraintDateWithStartDate(dto, value)` → dispatch, with no
 * branching on the constraint type at every site. The function is a no-op
 * for non-ALAP tasks, so it's safe to call unconditionally.
 *
 * The constraint TYPE stays `aslateaspossible` — only the date moves. The
 * spec ("activity stays at the chosen date") doesn't require switching
 * the type; keeping ALAP semantics preserves any downstream-cascade
 * scheduling behaviour the BE applies to ALAP tasks.
 *
 * @param {object} dto - DTO returned by formatRecordAsFixedDimensionDTO,
 *   mutated in place. Must carry `constraintType` (added by the caller
 *   before this runs — see `updateTask` in Gantt.js for the canonical
 *   "preserve constraint" pre-step).
 * @param {Date|string|number|null|undefined} newStartDate - The start
 *   date the user just chose. May already be timezone-normalised by the
 *   caller (any of removeTimezoneOffset's outputs round-trip safely).
 * @returns {object} the same DTO reference, for fluent use.
 */
export const syncAlapConstraintDateWithStartDate = (dto, newStartDate) => {
  if (!dto) return dto;
  if (dto.constraintType !== CONSTRAINT_TYPE_AS_LATE_AS_POSSIBLE_FRONT) return dto;
  if (newStartDate == null) return dto;
  dto.constraintDate = removeTimezoneOffset(newStartDate);
  return dto;
};

export const taskRecordIsSummary = (taskRecord) => {
  const lineTypeIsS = taskRecord.data?.lineType === SUMMARY_TYPE;
  const taskRecordIsParent = taskRecord.isParent;
  return lineTypeIsS || taskRecordIsParent;
};

// A task whose end date is before the Date of Review and whose progress is
// 0 (or unset) — shared by the Roadmap widget (LightweightTimeline) and the
// Bryntum Gantt's Name column renderer, so both surfaces agree on the exact
// same rule.
/**
 * The progress sent back to the backend. It used to be rounded to a whole
 * percent, which put the saved value — and the hours derived from it — up to
 * half a point away from the grey zone the user had just dragged. Two decimals,
 * like the backend's own Date-of-Review progress.
 */
export const toProgressValue = (percentDone) => Math.round(percentDone * 100) / 100;

export const isPastZeroProgress = (task, dateOfReview) => {
  if (!task || !dateOfReview || !task.endDate) return false;
  const progress = task.percentDone ?? 0;
  return new Date(task.endDate) < new Date(dateOfReview) && progress === 0;
};

export const isDimensionalResourceAssigned = (event) => {
  const valueToCheck = event.editorContext.column.originalData.id;
  const keys = Object.keys(
    event.editorContext.record.originalData.ganttResourceAllocation
  );
  return keys.includes(valueToCheck.toString());
};

export function formatRecordAsFixedDimensionDTO(taskRecord) {
  const resourceAllocationEntries = taskRecord.assignments
    .map((assignment) => [assignment.resourceId, assignment.units])
    .filter(([key]) => !!key);
  const resourceAllocation = Object.fromEntries(resourceAllocationEntries);
  // Backend contract: duration in days, effort in hours. With display
  // units active, editors can leave a week/month (or day-of-work) united
  // magnitude on the record — convert here as the last line of defense
  // for every DTO path (cell edit, drag, task-edit dialog).
  const durationDays = fromDisplayDuration(
    taskRecord.duration,
    taskRecord.durationUnit || "day"
  );
  const effortHours = fromDisplayWork(
    taskRecord.effort,
    taskRecord.effortUnit || "hour"
  );
  const duration =
    durationDays < MAXIMUM_SAFE_DURATION
      ? durationDays
      : taskRecord.originalData.duration;
  return {
    sourceId: taskRecord.id,
    fixedDimension: taskRecord.schedulingMode,
    resourceAllocation,
    duration,
    effort: effortHours,
    startDate: removeTimezoneOffset(taskRecord.startDate),
    endDate: removeTimezoneOffset(taskRecord.endDate),
    earlyStartDate: removeTimezoneOffset(taskRecord.earlyStartDate),
    earlyEndDate: removeTimezoneOffset(taskRecord.earlyEndDate),
    lateStartDate: removeTimezoneOffset(taskRecord.lateStartDate),
    lateEndDate: removeTimezoneOffset(taskRecord.lateEndDate),
    deadlineDate:
      taskRecord.deadlineDate == null
        ? null
        : removeTimezoneOffset(taskRecord.deadlineDate),
  };
}

export function extractIdsOfTasksTree(tasksTree) {
  return extractFieldWithRecursion(tasksTree, "id", "children");
}

/**
 * Brings both stores back in line with the server's sequence after a gesture.
 *
 * The response carries `parentSaId` — a row id — alongside the ordinals, so the
 * rows are re-parented on an identity rather than on a number the backend
 * renumbers across the whole scenario on every add, move-to and reorder. That
 * also removes the reason this used to wait 200 ms before touching the store:
 * the wait existed so the store would still hold the PREVIOUS numbering when the
 * ordinals were resolved against it, which is not a thing that can be relied on
 * once someone else is editing the same scenario.
 *
 * `dispatch` is not optional. Refreshing the Bryntum records alone left the Redux
 * rows on the numbering they were read with, so the client held two generations
 * of ordinals at once — and every later incremental pass re-parented the whole
 * Gantt from the Redux one. A row whose ordinal had since been handed to another
 * row moved under it, subtree included; on a scenario holding two copies of the
 * same scope the drift is the size of what was inserted, so it landed on the twin.
 */
export function realignSequenceOnGantt(
  taskStore,
  scenarioId,
  dispatch,
  atUxId = 3
) {
  return axios
    .get(`${PDA_PREFIX}/schedule-activity/aligned-sequence/${scenarioId}/${atUxId}`)
    .then(({ data }) => {
      data.forEach(({ id, uxId, parentUxId }) => {
        realignSequenceForRecord(taskStore.getById(id), uxId, parentUxId);
      });
      // Writing the scalars leaves the row where it was: the ordinals describe the
      // hierarchy, they do not apply it. Without this the store disagreed with the
      // server until a full refetch.
      reconcileTaskParents(taskStore, data);
      dispatch({ type: REALIGN_SCHEDULE_ACTIVITY_SEQUENCE, payload: data });
    });
}

function realignSequenceForRecord(record, newUxId, newParentUxId) {
  if (!record) return;

  const uxIdChanged = record.uxId !== newUxId,
    parentUxIdChanged = record.parentUxId !== newParentUxId;
  if (uxIdChanged) {
    record.uxId = newUxId;
    record.data.uxId = newUxId;
  }
  if (parentUxIdChanged) {
    record.parentUxId = newParentUxId;
    record.data.parentUxId = newParentUxId;
  }
  if (uxIdChanged || parentUxIdChanged) record.commitAsync();
}
